import { createStyles, makeStyles, Menu, MenuItem } from "@material-ui/core"
import clsx from "clsx"
import React, { useState } from "react"
import { useLocation } from "react-router"
import { ROUTES, useNavigation } from "../../hooks/useNavigation"
import { useSession } from "../../hooks/useSession"
import { useUser } from "../../hooks/useUser"
import { colors } from "../../utils/theme"
import ChevronDownIcon from "../atoms/ChevronDownIcon"
import ChevronUpIcon from "../atoms/ChevronUpIcon"
import Logo from "../atoms/Logo"
import NavigationButton from "../atoms/NavigationButton"
import { Role } from "../../utils/constants"

export const HeaderHeight = 72

const useStyles = makeStyles((theme) =>
  createStyles({
    header: {
      backgroundColor: "#EEF5FD",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      height: HeaderHeight,
      position: "fixed",
      zIndex: 1000,
      width: "100%",
    },
    name: {
      marginRight: 4,
    },
    button: {
      fontFamily: "Lato",
      fontSize: 16,
      color: colors.neutral.space,
      backgroundColor: "transparent",
      padding: 0,
      margin: 0,
      border: 0,
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      "&:focus": {
        outline: "none",
      },
    },
    menuPaper: {
      borderRadius: 4,
      border: "1px solid rgba(0, 0, 0, 0.1)",
      backgroundColor: colors.neutral.white,
      boxShadow:
        "0px 0px 4px rgba(54, 53, 69, 0.1), 0px 3px 12px rgba(54, 53, 69, 0.05)",
    },
    logo: {
      margin: 0,
      padding: 0,
      border: 0,
      backgroundColor: "transparent",
      cursor: "pointer",
      "&:focus": {
        outline: "none",
      },
    },
    controls: {
      display: "flex",
      alignItems: "center",
    },
    partner: {
      display: "none",
    },
  })
)

function Header() {
  const classes = useStyles()
  const { logout, session } = useSession()
  const { toAccountSettings } = useNavigation()
  const { user } = useUser()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const {
    toMyOrg,
    toDashboard,
    toOrgSettings,
    toTechniquesAndInterventions,
    toPractitioners,
  } = useNavigation()
  const location = useLocation()

  const { search } = location
  const queryParams = new URLSearchParams(search)

  const partner = queryParams.get("partner")
  const isPartner = partner === "true"

  return (
    <header className={clsx(isPartner ? classes.partner : classes.header)}>
      <button
        className={classes.logo}
        onClick={user?.isOrgAdmin ? toMyOrg : toDashboard}
      >
        <Logo />
      </button>
      <div className={classes.controls}>
        {user?.isOrgAdmin && (
          <>
            {user?.email?.endsWith("@ksanahealth.com") && (
              <NavigationButton
                onClick={toTechniquesAndInterventions}
                active={
                  location.pathname.includes(ROUTES.TECHNIQUES_INTERVENTIONS) ||
                  location.pathname.includes("practitioner-detail")
                }
              >
                My Techniques and Interventions
              </NavigationButton>
            )}
            <NavigationButton
              onClick={toMyOrg}
              active={
                location.pathname.includes(ROUTES.MY_ORG) ||
                location.pathname.includes("practitioner-detail")
              }
            >
              My Organization
            </NavigationButton>
          </>
        )}
        {session?.role === Role.WEB_ROLE && (
          <NavigationButton
            active={
              location.pathname.includes(ROUTES.MY_PRACTITIONERS) ||
              location.pathname.includes("practitioner")
            }
            onClick={toPractitioners}
          >
            My Practitioners
          </NavigationButton>
        )}
        <NavigationButton
          active={
            location.pathname.includes(ROUTES.MY_CLIENTS) ||
            location.pathname.includes("client-detail")
          }
          onClick={toDashboard}
        >
          My Clients
        </NavigationButton>

        <button
          className={classes.button}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <div className={classes.name}>
            {user?.firstName} {user?.lastName}
          </div>
          {!anchorEl ? <ChevronDownIcon /> : <ChevronUpIcon />}
        </button>
        <Menu
          anchorEl={anchorEl}
          keepMounted={true}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          classes={{
            paper: classes.menuPaper,
          }}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              toAccountSettings()
            }}
          >
            Account Settings
          </MenuItem>
          {user?.isOrgAdmin && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
                toOrgSettings()
              }}
            >
              Organization Settings
            </MenuItem>
          )}
          <MenuItem onClick={logout}>Log Out</MenuItem>
        </Menu>
      </div>
    </header>
  )
}

export default Header
